import {
  useChatSocket,
  type UseChatSocket,
} from "@sill/shared/chat/useChatSocket";

import type { ClientFrame, ServerFrame } from "../lib/agent-types";
import { useAdvisorStore } from "../stores/advisor";

// Thin consumer-specific wrapper over the generic `useChatSocket` (extracted
// to `frontend/shared/src/chat/useChatSocket.ts` — plan 188 W2-M8, task 801).
// Public API (call signature, return shape, connection/reconnect/pending-
// frame behavior) is byte-identical to the pre-extraction implementation;
// only the frame-handling callback and default WS path are now passed
// explicitly instead of being hard-wired inside the hook body. The Pro CRM
// Copilot dock (M8b) will have its own equally-thin wrapper pointed at
// `/ws/professional/chat` and its own store.
export type AdvisorSocketStatus = "connecting" | "open" | "closed";
export type UseAdvisorSocket = UseChatSocket<ClientFrame>;

type Options = {
  /** Called fresh per connection attempt — never pass a captured token string. */
  getToken: () => Promise<string | null>;
  enabled?: boolean;
  url?: string;
};

export function useAdvisorSocket({
  getToken,
  enabled,
  url,
}: Options): UseAdvisorSocket {
  const handleServerFrame = useAdvisorStore((state) => state.handleServerFrame);

  return useChatSocket<ServerFrame, ClientFrame>({
    getToken,
    enabled,
    url,
    defaultPath: "/ws/agent/chat",
    onServerFrame: handleServerFrame,
    // Defense in depth: if the socket closes while a turn is still streaming
    // (the backend rejected the handshake and its error frame never arrived,
    // or the connection dropped mid-response), surface it as an error frame so
    // the composer stops showing "thinking" forever. Only fires when a turn was
    // actually in flight — a normal idle disconnect is silent. The backend's
    // graceful close usually delivers a precise error first; this catches the
    // cases where no terminal frame reaches the client at all.
    onUnexpectedClose: () => {
      if (!useAdvisorStore.getState().isStreaming) return;
      handleServerFrame({
        type: "error",
        code: "connection_closed",
        error:
          "The connection dropped before a response arrived. Please try again.",
      } as ServerFrame);
    },
  });
}
